import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { LimePageHeader } from "@/Components/LimePageHeader";
import { api } from "@/lib/api-client";
import { type UseFormReturnType, useForm } from "@mantine/form";
import { useEffect } from "react";
import type {
  GetClientSettings,
  PutClientSettings,
} from "../../../../../server/src/types";
import {
  addToast,
  Button,
  Divider,
  Link,
  Snippet,
  Spinner,
} from "@heroui/react";
import { DurationInput } from "@/Components/NextBase/DurationInput";
import { LimeSwitch } from "@/Components/NextBase/LimeSwitch";
import { LimeNumberInput } from "@/Components/NextBase/LimeNumberInput";
import { LimeCollapse } from "@/Components/NextBase/LimeCollapse";

type ClientSettingsForm = {
  restrictLunchTime: boolean;
  maxLunchTimeMinutes: number | null;

  rescheduleMinRescheduleHours: number;
  rescheduleMinCancelHours: number;

  allowDuplicateCustomerOnAppointment: boolean;

  restrictNumFutureApptsForCustomer: boolean;
  maxNumFutureAppointmentsForCustomer: number | null;
  rescheduleCanSeePastAppointments: boolean;

  showComplexDurationPicker: boolean;
};
export const ClientSettings = () => {
  const {
    data: settings,
    isPending: isSettingsPending,
    isSuccess: isSettingsSuccess,
  } = api.client.useClientSettings();

  const { mutateAsync: updateSettings, isPending: isUpdateSettingsPending } =
    api.client.usePutClientSettings();

  const form = useForm<ClientSettingsForm>({
    mode: "controlled",
    initialValues: {
      restrictLunchTime: false,
      maxLunchTimeMinutes: null,
      rescheduleMinCancelHours: 0,
      rescheduleMinRescheduleHours: 0,
      allowDuplicateCustomerOnAppointment: false,
      restrictNumFutureApptsForCustomer: false,
      maxNumFutureAppointmentsForCustomer: null,
      rescheduleCanSeePastAppointments: true,
      showComplexDurationPicker: false,
    },

    validate: {
      maxLunchTimeMinutes: (value) => {
        if (form.getValues().restrictLunchTime && (!value || value === 0)) {
          return t`Polje je obvezno`;
        }
      },
    },
  });

  useEffect(() => {
    if (settings) {
      form.setValues({
        restrictLunchTime: Boolean(settings.maxLunchTimeMinutes),
        maxLunchTimeMinutes: settings.maxLunchTimeMinutes,
        rescheduleMinCancelHours: settings.rescheduleMinCancelHours,
        rescheduleMinRescheduleHours: settings.rescheduleMinRescheduleHours,
        allowDuplicateCustomerOnAppointment:
          settings.allowDuplicateCustomerOnAppointment,
        maxNumFutureAppointmentsForCustomer:
          settings.maxNumFutureAppointmentsForCustomer,
        restrictNumFutureApptsForCustomer: Boolean(
          settings.maxNumFutureAppointmentsForCustomer,
        ),
        rescheduleCanSeePastAppointments:
          settings.rescheduleCanSeePastAppointments,
        showComplexDurationPicker: settings.showComplexDurationPicker,
      });
    }
  }, [settings]);

  const handleSubmitForm = async (values: ClientSettingsForm) => {
    const data: PutClientSettings["body"] = {
      ...values,
      maxLunchTimeMinutes: values.restrictLunchTime
        ? values.maxLunchTimeMinutes
        : null,
      maxNumFutureAppointmentsForCustomer:
        values.restrictNumFutureApptsForCustomer
          ? values.maxNumFutureAppointmentsForCustomer
          : null,
      rescheduleCanSeePastAppointments: values.rescheduleCanSeePastAppointments,
    };

    await updateSettings(data);

    addToast({
      title: t`Nastavitve so bile uspešno shranjene`,
      color: "success",
    });
  };

  return (
    <>
      <LimePageHeader title={t`Nastavitve`} />

      {isSettingsPending && <Spinner />}

      {isSettingsSuccess && (
        <div>
          <form onSubmit={form.onSubmit(handleSubmitForm)}>
            <div className="mt-4 max-w-[550px] px-4 md:px-8">
              <GeneralSettings form={form} />
            </div>

            <Divider className="my-2" />

            <div className="mt-4 max-w-[550px] px-4 md:px-8">
              <FormLinks hashes={settings.formHashes} />
            </div>

            <Divider className="my-2" />

            <div className="mt-4 max-w-[550px] px-4 md:px-8">
              <ScheduleSettings form={form} />
            </div>

            <Divider className="my-2" />

            <div className="mt-4 max-w-[550px] px-4 md:px-8">
              <RescheduleSettings form={form} />
            </div>

            <Divider className="my-2" />

            <div className="mt-4 max-w-[550px] px-4 md:px-8">
              <BreakSettings form={form} />
            </div>

            <Divider className="my-2" />

            <div className="mt-4 max-w-[550px] px-4 md:px-8">
              <Button
                type={"submit"}
                isLoading={isUpdateSettingsPending}
                color="primary"
              >
                <Trans>Shrani</Trans>
              </Button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

const FormLinks = ({
  hashes,
}: {
  hashes: GetClientSettings["response"]["formHashes"];
}) => {
  return (
    <>
      <p className="mb-2 text-lg font-medium">
        <Trans>Povezave do form</Trans>
      </p>

      <div className="flex flex-col gap-2">
        {hashes.map((hash) => {
          const formLink = `${import.meta.env.VITE_FORM_URL}/${hash}`;

          return (
            <div className="flex gap-2" key={hash}>
              <Snippet
                hideSymbol
                codeString={formLink}
                variant="flat"
                className="gap-0"
              ></Snippet>

              <Link
                key={hash}
                href={formLink}
                target={"_blank"}
                rel={"noreferrer"}
                showAnchorIcon
              >
                {hash}
              </Link>
            </div>
          );
        })}
      </div>
    </>
  );
};

const ScheduleSettings = ({
  form,
}: {
  form: UseFormReturnType<ClientSettingsForm>;
}) => {
  return (
    <>
      <p className="mb-2 text-lg font-medium">
        <Trans>Nastavitve naročanja</Trans>
      </p>

      <LimeSwitch
        title={t`Pokaži kompleksni izbor časa (dnevi, ure, minute)`}
        {...form.getInputProps("showComplexDurationPicker", {
          type: "checkbox",
        })}
        isCompact
      />
    </>
  );
};

const RescheduleSettings = ({
  form,
}: {
  form: UseFormReturnType<ClientSettingsForm>;
}) => {
  return (
    <div className="flex flex-col gap-2">
      <p className="mb-2 text-lg font-medium">
        <Trans>Nastavitve prenaročanja</Trans>
      </p>

      <LimeNumberInput
        label={t`Minimalno število ur pred prenaročanjem`}
        {...form.getInputProps("rescheduleMinRescheduleHours")}
      />

      <LimeNumberInput
        label={t`Minimalno število ur pred odpovedjo`}
        {...form.getInputProps("rescheduleMinCancelHours")}
      />

      <LimeSwitch
        title={t`Ali lahko stranka vidi svoje pretekle termine?`}
        {...form.getInputProps("rescheduleCanSeePastAppointments", {
          type: "checkbox",
        })}
        isCompact
      />

      <LimeSwitch
        title={t`Omeji koliko terminov vnaprej lahko ima stranka rezerviranih`}
        {...form.getInputProps("restrictNumFutureApptsForCustomer", {
          type: "checkbox",
        })}
        isCompact
      />

      <LimeCollapse isOpen={form.getValues().restrictNumFutureApptsForCustomer}>
        <LimeNumberInput
          label={t`Največje število terminov rezerviranih vnaprej`}
          minValue={0}
          step={5}
          {...form.getInputProps("maxNumFutureAppointmentsForCustomer")}
        />
      </LimeCollapse>
    </div>
  );
};

const BreakSettings = ({
  form,
}: {
  form: UseFormReturnType<ClientSettingsForm>;
}) => {
  return (
    <>
      <p className="mb-2 text-lg font-medium">
        <Trans>Nastavitve pavz</Trans>
      </p>

      <LimeSwitch
        title={t`Omeji dolžino malice`}
        {...form.getInputProps("restrictLunchTime", {
          type: "checkbox",
        })}
        isCompact
      />

      <LimeCollapse isOpen={form.getValues().restrictLunchTime}>
        <DurationInput
          label={t`Največja dolžina malice`}
          value={form.getValues().maxLunchTimeMinutes ?? undefined}
          onValueChange={(value) => {
            form.setFieldValue("maxLunchTimeMinutes", value);
          }}
        />
      </LimeCollapse>
    </>
  );
};

const GeneralSettings = ({
  form,
}: {
  form: UseFormReturnType<ClientSettingsForm>;
}) => {
  return (
    <>
      <p className="mb-2 text-lg font-medium">
        <Trans>Splošno</Trans>
      </p>

      <LimeSwitch
        title={t`Privzeto dovoli podvojene stranke na terminih`}
        {...form.getInputProps("allowDuplicateCustomerOnAppointment", {
          type: "checkbox",
        })}
        isCompact
      />
    </>
  );
};
